import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Loader2, BarChart3, Building2, UsersRound, Calendar, Check, X, Mail, Download, Upload } from "lucide-react";
import { ACTIVITY_LABELS } from "@/lib/activities";
import { ENTITY_TYPES } from "@/lib/entityTypes";
import { ImageUploader, GalleryUploader } from "@/components/ImageUploader";

const EMPTY = {
  name: "", contact_person: "", address: "", city: "", province: "", region: "", postal_code: "",
  country: "Italia", lat: 41.9, lng: 12.5, phone: "", email: "", website: "",
  facebook: "", instagram: "",
  entity_type: "", entity_type_custom: "",
  description: "", activities: [], cover_image: "", gallery: [],
};

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("companies");
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [activities, setActivities] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editingProject, setEditingProject] = useState(null);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/login");
  }, [user, loading, navigate]);

  const loadAll = () => Promise.all([
    api.get("/admin/stats").then((r) => setStats(r.data)),
    api.get("/companies").then((r) => setCompanies(r.data)),
    api.get("/bookings").then((r) => setBookings(r.data)),
    api.get("/activities").then((r) => setActivities(r.data)),
    api.get("/admin/memberships").then((r) => setMemberships(r.data)).catch(() => {}),
    api.get("/admin/suggestions").then((r) => setSuggestions(r.data)).catch(() => {}),
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}),
  ]);

  useEffect(() => { if (user?.role === "admin") loadAll(); }, [user]);

  const save = async (c) => {
    try {
      if (c.id) await api.put(`/companies/${c.id}`, c);
      else await api.post("/companies", c);
      toast.success("Salvato");
      setEditing(null);
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questa fattoria?")) return;
    try { await api.delete(`/companies/${id}`); toast.success("Eliminata"); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const setStatus = async (id, status) => {
    try { await api.put(`/bookings/${id}/status?status=${status}`); toast.success("Stato aggiornato"); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const setSuggestionStatus = async (id, status) => {
    try { await api.put(`/admin/suggestions/${id}/status?status=${status}`); toast.success("Stato aggiornato"); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const saveProject = async (p) => {
    try {
      if (p.id) await api.put(`/projects/${p.id}`, p);
      else await api.post("/projects", p);
      toast.success("Progetto salvato");
      setEditingProject(null);
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const delProject = async (id) => {
    if (!window.confirm("Eliminare questo progetto?")) return;
    try { await api.delete(`/projects/${id}`); toast.success("Progetto eliminato"); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const handleExport = async () => {
    try {
      const { data } = await api.get("/admin/backup/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const date = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `agrigiochiamo-backup-${date}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const totalItems = Object.values(data.counts || {}).reduce((s, n) => s + n, 0);
      toast.success(`Backup completo scaricato: ${totalItems} record totali`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);

      // Support both new full backup (kind=agrigiochiamo-full-backup) and legacy companies-only
      const isFullBackup = parsed?.kind === "agrigiochiamo-full-backup" && parsed?.collections;
      const isLegacyCompanies = Array.isArray(parsed?.companies) || Array.isArray(parsed);

      if (!isFullBackup && !isLegacyCompanies) {
        toast.error("File non valido: non riconosciuto come backup Agrigiochiamo");
        return;
      }

      const label = isFullBackup
        ? `Backup completo trovato:\n${Object.entries(parsed.counts || {}).map(([k, v]) => `  • ${k}: ${v}`).join("\n")}`
        : `Trovato backup legacy con ${(parsed.companies || parsed).length} fattorie.`;

      const mode = window.confirm(
        `${label}\n\n` +
        `OK → SOSTITUISCI tutto (cancella dati esistenti prima di importare)\n` +
        `Annulla → UPSERT (aggiorna esistenti per id, aggiunge le nuove)`
      ) ? "replace" : "upsert";

      let result;
      if (isFullBackup) {
        const { data } = await api.post("/admin/backup/import", {
          collections: parsed.collections,
          mode,
        });
        result = data;
      } else {
        const list = Array.isArray(parsed) ? parsed : parsed.companies;
        const { data } = await api.post("/admin/companies/import", { companies: list, mode });
        result = data;
      }

      const inserted = typeof result.inserted === "object"
        ? Object.values(result.inserted).reduce((s, n) => s + n, 0)
        : result.inserted;
      const updated = typeof result.updated === "object"
        ? Object.values(result.updated).reduce((s, n) => s + n, 0)
        : result.updated;
      toast.success(`Importato: ${inserted} nuovi record, ${updated} aggiornati (${mode})`);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e) || "Errore lettura file");
    }
  };

  if (!user || user.role !== "admin") return null;

  return (
    <div className="mx-auto max-w-7xl px-6 md:px-10 py-10 md:py-14">
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#C8795A] mb-2">Pannello Amministrazione</div>
        <h1 className="font-display font-black text-4xl md:text-5xl text-[#2C312E]">Gestione Network</h1>
      </div>

      {stats && (
        <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <Stat icon={Building2} label="Fattorie" value={stats.companies} color="#4A6B53"/>
          <Stat icon={UsersRound} label="Utenti" value={stats.users} color="#C8795A"/>
          <Stat icon={Calendar} label="Prenotazioni" value={stats.bookings} color="#E4B871"/>
          <Stat icon={BarChart3} label="In attesa" value={stats.pending_bookings} color="#2C312E"/>
          <Stat icon={UsersRound} label="Adesioni pagate" value={`${stats.memberships_paid}/${stats.memberships}`} color="#4A6B53"/>
          <Stat icon={Mail} label="Segnalazioni nuove" value={`${stats.suggestions_new}/${stats.suggestions}`} color="#C8795A"/>
        </div>
      )}
      <div className="inline-flex rounded-full bg-white border-2 border-[#E5DCD0] p-1 mb-6 flex-wrap">
        <TabBtn active={tab === "companies"} onClick={() => setTab("companies")} testid="admin-tab-companies">Fattorie</TabBtn>
        <TabBtn active={tab === "bookings"} onClick={() => setTab("bookings")} testid="admin-tab-bookings">Prenotazioni</TabBtn>
        <TabBtn active={tab === "memberships"} onClick={() => setTab("memberships")} testid="admin-tab-memberships">Adesioni</TabBtn>
        <TabBtn active={tab === "suggestions"} onClick={() => setTab("suggestions")} testid="admin-tab-suggestions">Segnalazioni{stats?.suggestions_new ? ` (${stats.suggestions_new})` : ""}</TabBtn>
        <TabBtn active={tab === "projects"} onClick={() => setTab("projects")} testid="admin-tab-projects">Progetti</TabBtn>
      </div>

      {tab === "companies" && (
        <div>
          <div className="flex justify-end mb-4 gap-2 flex-wrap">
            <button
              onClick={handleExport}
              className="inline-flex items-center gap-2 rounded-full border-2 border-[#E5DCD0] px-4 py-2.5 text-sm font-bold hover:border-[#4A6B53] hover:text-[#4A6B53]"
              data-testid="export-companies-btn"
              title="Scarica backup completo (fattorie, progetti, prenotazioni, adesioni, segnalazioni, utenti)"
            >
              <Download size={16}/> Esporta tutto
            </button>
            <label className="inline-flex items-center gap-2 rounded-full border-2 border-[#E5DCD0] px-4 py-2.5 text-sm font-bold hover:border-[#C8795A] hover:text-[#C8795A] cursor-pointer" data-testid="import-companies-label" title="Importa backup JSON (supporta full backup o legacy fattorie)">
              <Upload size={16}/> Importa
              <input
                type="file"
                accept="application/json"
                className="hidden"
                onChange={handleImport}
                data-testid="import-companies-input"
              />
            </label>
            <button onClick={() => setEditing({ ...EMPTY })} className="inline-flex items-center gap-2 rounded-full bg-[#4A6B53] text-white px-5 py-2.5 text-sm font-bold hover:bg-[#3D5844]" data-testid="new-company-btn">
              <Plus size={16}/> Nuova fattoria
            </button>
          </div>
          <div className="rounded-3xl bg-white border border-[#E5DCD0] overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#F2EBE1] text-left text-xs uppercase font-bold tracking-wider text-[#7A7369]">
                <tr>
                  <th className="px-5 py-4">Nome</th>
                  <th className="px-5 py-4 hidden md:table-cell">Città</th>
                  <th className="px-5 py-4 hidden lg:table-cell">Attività</th>
                  <th className="px-5 py-4 w-32 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5DCD0]">
                {companies.map((c) => (
                  <tr key={c.id} className="hover:bg-[#FBF9F6]" data-testid={`admin-company-${c.id}`}>
                    <td className="px-5 py-4 font-bold text-[#2C312E]">{c.name}</td>
                    <td className="px-5 py-4 text-sm text-[#58625B] hidden md:table-cell">{c.city} ({c.province})</td>
                    <td className="px-5 py-4 hidden lg:table-cell"><span className="text-xs text-[#58625B]">{c.activities?.length || 0} attività</span></td>
                    <td className="px-5 py-4 text-right">
                      <div className="inline-flex gap-2">
                        <button onClick={() => setEditing(c)} className="p-2 rounded-xl border border-[#E5DCD0] hover:border-[#4A6B53] hover:text-[#4A6B53]" data-testid={`edit-${c.id}`}><Pencil size={14}/></button>
                        <button onClick={() => del(c.id)} className="p-2 rounded-xl border border-[#E5DCD0] hover:border-[#C8795A] hover:text-[#C8795A]" data-testid={`del-${c.id}`}><Trash2 size={14}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "bookings" && (
        <div className="space-y-3">
          {bookings.length === 0 ? (
            <div className="rounded-3xl border-2 border-dashed border-[#E5DCD0] p-10 text-center text-[#58625B]">Nessuna richiesta</div>
          ) : bookings.map((b) => (
            <div key={b.id} className="rounded-3xl bg-white border border-[#E5DCD0] p-6" data-testid={`admin-booking-${b.id}`}>
              <div className="flex flex-wrap justify-between gap-3 mb-3">
                <div>
                  <div className="font-display font-black text-lg text-[#2C312E]">{b.company_name}</div>
                  <div className="text-sm text-[#58625B]">Richiesta da <span className="font-bold text-[#2C312E]">{b.visitor_name}</span> ({b.visitor_type})</div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusPill status={b.status}/>
                  {b.status === "pending" && (
                    <>
                      <button onClick={() => setStatus(b.id, "confirmed")} className="inline-flex items-center gap-1 text-xs font-bold rounded-full bg-[#4A6B53] text-white px-3 py-1.5 hover:bg-[#3D5844]" data-testid={`confirm-${b.id}`}><Check size={12}/>Conferma</button>
                      <button onClick={() => setStatus(b.id, "cancelled")} className="inline-flex items-center gap-1 text-xs font-bold rounded-full border border-[#E5DCD0] px-3 py-1.5 hover:border-[#C8795A] hover:text-[#C8795A]" data-testid={`cancel-${b.id}`}><X size={12}/>Annulla</button>
                    </>
                  )}
                </div>
              </div>
              <div className="grid md:grid-cols-4 gap-3 text-sm text-[#58625B]">
                <div><span className="text-xs uppercase tracking-wider text-[#7A7369]">Data</span><div className="font-bold text-[#2C312E]">{b.preferred_date}</div></div>
                <div><span className="text-xs uppercase tracking-wider text-[#7A7369]">Persone</span><div className="font-bold text-[#2C312E]">{b.group_size}</div></div>
                <div className="md:col-span-2 truncate"><span className="text-xs uppercase tracking-wider text-[#7A7369]">Contatto</span><div className="font-semibold">{b.visitor_email} · {b.visitor_phone}</div></div>
              </div>
              {b.activities?.length > 0 && <div className="mt-3 flex flex-wrap gap-1.5">{b.activities.map((a) => <span key={a} className="rounded-full bg-[#F2EBE1] text-[#4A6B53] px-2 py-0.5 text-[11px] font-bold">{ACTIVITY_LABELS[a] || a}</span>)}</div>}
              {b.message && <div className="mt-3 text-sm italic text-[#58625B]">« {b.message} »</div>}
              {b.company_email && (
                <a href={`mailto:${b.company_email}?subject=Richiesta di visita ${b.company_name}&body=${encodeURIComponent(`Da: ${b.visitor_name} (${b.visitor_email})\nTelefono: ${b.visitor_phone}\nData: ${b.preferred_date}\nPersone: ${b.group_size}\n\n${b.message}`)}`}
                  className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#C8795A] hover:underline">
                  <Mail size={12}/> Inoltra alla fattoria
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "memberships" && (
        <div className="space-y-3">
          {memberships.length === 0 ? (
            <div className="rounded-3xl border-2 border-dashed border-[#E5DCD0] p-10 text-center text-[#58625B]">Nessuna richiesta di adesione</div>
          ) : memberships.map((m) => (
            <div key={m.id} className="rounded-3xl bg-white border border-[#E5DCD0] p-6" data-testid={`admin-membership-${m.id}`}>
              <div className="flex flex-wrap justify-between gap-3 mb-3">
                <div>
                  <div className="font-display font-black text-lg text-[#2C312E]">
                    {m.is_legal_entity && m.company_name ? m.company_name : `${m.first_name} ${m.last_name}`}
                  </div>
                  <div className="text-sm text-[#58625B]">
                    {m.is_legal_entity ? "Persona giuridica" : "Persona fisica"} · {m.email} · {m.phone}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="rounded-full px-3 py-1 text-[11px] font-bold bg-[#F2EBE1] text-[#4A6B53]">€{m.amount}</span>
                  <span className={`rounded-full px-3 py-1 text-[11px] font-bold ${m.payment_status === "paid" ? "bg-[#4A6B53] text-white" : m.payment_status === "expired" ? "bg-[#E5DCD0] text-[#7A7369]" : "bg-[#E4B871]/30 text-[#8B6514]"}`}>
                    {m.payment_status === "paid" ? "PAGATO" : m.payment_status === "expired" ? "SCADUTO" : "IN ATTESA"}
                  </span>
                </div>
              </div>
              <div className="grid md:grid-cols-4 gap-3 text-sm">
                <div><span className="text-xs uppercase tracking-wider text-[#7A7369]">Tipo</span><div className="font-bold text-[#2C312E]">{m.membership_type === "new" ? "Nuovo socio" : "Rinnovo"}</div></div>
                <div><span className="text-xs uppercase tracking-wider text-[#7A7369]">Kit</span><div className="font-bold text-[#2C312E] capitalize">{m.kit_choice}</div></div>
                <div className="md:col-span-2"><span className="text-xs uppercase tracking-wider text-[#7A7369]">Gruppi</span><div className="text-[#58625B]">{m.working_groups?.join(", ") || "—"}</div></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "suggestions" && (
        <div className="space-y-3">
          {suggestions.length === 0 ? (
            <div className="rounded-3xl border-2 border-dashed border-[#E5DCD0] p-10 text-center text-[#58625B]">Nessuna segnalazione ricevuta</div>
          ) : suggestions.map((s) => (
            <div key={s.id} className="rounded-3xl bg-white border border-[#E5DCD0] p-6" data-testid={`admin-suggestion-${s.id}`}>
              <div className="flex flex-wrap justify-between gap-3 mb-3">
                <div>
                  <div className="font-display font-black text-lg text-[#2C312E]">{s.farm_name}</div>
                  <div className="text-sm text-[#58625B]">{s.city}</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <SuggestionPill status={s.status}/>
                  {s.status !== "contacted" && (
                    <button onClick={() => setSuggestionStatus(s.id, "contacted")} className="inline-flex items-center gap-1 text-xs font-bold rounded-full bg-[#4A6B53] text-white px-3 py-1.5 hover:bg-[#3D5844]" data-testid={`mark-contacted-${s.id}`}>
                      <Check size={12}/> Contattata
                    </button>
                  )}
                  {s.status !== "dismissed" && (
                    <button onClick={() => setSuggestionStatus(s.id, "dismissed")} className="inline-flex items-center gap-1 text-xs font-bold rounded-full border border-[#E5DCD0] px-3 py-1.5 hover:border-[#C8795A] hover:text-[#C8795A]" data-testid={`dismiss-${s.id}`}>
                      <X size={12}/> Scarta
                    </button>
                  )}
                </div>
              </div>
              <p className="text-sm text-[#58625B] italic leading-relaxed">« {s.description} »</p>
              {(s.submitter_name || s.submitter_email) && (
                <div className="mt-3 pt-3 border-t border-[#E5DCD0] text-sm text-[#58625B]">
                  Segnalato da <span className="font-bold text-[#2C312E]">{s.submitter_name || "—"}</span>
                  {s.submitter_email && <> · {s.submitter_email}</>}
                  <span className="text-xs ml-2">({s.submitter_type})</span>
                </div>
              )}
              <a
                href={`mailto:info@agrigiochiamo.it?subject=${encodeURIComponent("Segnalazione realtà: " + s.farm_name)}&body=${encodeURIComponent(`Realtà: ${s.farm_name}\nComune: ${s.city}\n\nDescrizione esperienza:\n${s.description}\n\nSegnalata da: ${s.submitter_name || "Anonimo"} (${s.submitter_email || "no email"}) — ${s.submitter_type}`)}`}
                className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#C8795A] hover:underline"
                data-testid={`forward-${s.id}`}
              >
                <Mail size={12}/> Inoltra a info@agrigiochiamo.it
              </a>
            </div>
          ))}
        </div>
      )}

      {tab === "projects" && (
        <div>
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setEditingProject({ title: "", excerpt: "", content: "", status: "current", start_date: "", end_date: "", cover_image: "", gallery: [], linked_companies: [] })}
              className="inline-flex items-center gap-2 rounded-full bg-[#4A6B53] text-white px-5 py-2.5 text-sm font-bold hover:bg-[#3D5844]"
              data-testid="new-project-btn"
            >
              <Plus size={16}/> Nuovo progetto
            </button>
          </div>
          {projects.length === 0 ? (
            <div className="rounded-3xl border-2 border-dashed border-[#E5DCD0] p-10 text-center text-[#58625B]">Nessun progetto pubblicato</div>
          ) : (
            <div className="rounded-3xl bg-white border border-[#E5DCD0] overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#F2EBE1] text-left text-xs uppercase font-bold tracking-wider text-[#7A7369]">
                  <tr>
                    <th className="px-5 py-4">Titolo</th>
                    <th className="px-5 py-4 hidden md:table-cell">Stato</th>
                    <th className="px-5 py-4 hidden lg:table-cell">Periodo</th>
                    <th className="px-5 py-4 w-32 text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5DCD0]">
                  {projects.map((p) => (
                    <tr key={p.id} className="hover:bg-[#FBF9F6]" data-testid={`admin-project-${p.id}`}>
                      <td className="px-5 py-4 font-bold text-[#2C312E]">{p.title}</td>
                      <td className="px-5 py-4 hidden md:table-cell">
                        <span className="inline-flex rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider bg-[#F2EBE1] text-[#4A6B53]">
                          {p.status === "current" ? "In corso" : p.status === "upcoming" ? "In arrivo" : "Realizzato"}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-sm text-[#58625B] hidden lg:table-cell">
                        {p.start_date || "—"}{p.end_date && ` — ${p.end_date}`}
                      </td>
                      <td className="px-5 py-4 text-right">
                        <div className="inline-flex gap-2">
                          <button onClick={() => setEditingProject(p)} className="p-2 rounded-xl border border-[#E5DCD0] hover:border-[#4A6B53] hover:text-[#4A6B53]" data-testid={`edit-project-${p.id}`}><Pencil size={14}/></button>
                          <button onClick={() => delProject(p.id)} className="p-2 rounded-xl border border-[#E5DCD0] hover:border-[#C8795A] hover:text-[#C8795A]" data-testid={`del-project-${p.id}`}><Trash2 size={14}/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {editing && <EditModal initial={editing} activities={activities} onClose={() => setEditing(null)} onSave={save}/>}
      {editingProject && <ProjectEditModal initial={editingProject} companies={companies} onClose={() => setEditingProject(null)} onSave={saveProject}/>}
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-3xl bg-white border border-[#E5DCD0] p-5 flex items-center gap-3">
      <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white" style={{ background: color }}><Icon size={20}/></div>
      <div><div className="font-display font-black text-2xl text-[#2C312E]">{value}</div><div className="text-[11px] font-bold uppercase tracking-wider text-[#7A7369]">{label}</div></div>
    </div>
  );
}
function TabBtn({ active, onClick, children, testid }) {
  return <button onClick={onClick} data-testid={testid} className={`rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${active ? "bg-[#4A6B53] text-white" : "text-[#58625B] hover:text-[#2C312E]"}`}>{children}</button>;
}
function StatusPill({ status }) {
  const cfg = {
    pending: { bg: "bg-[#E4B871]/25", fg: "text-[#8B6514]", l: "In attesa" },
    confirmed: { bg: "bg-[#4A6B53]/15", fg: "text-[#4A6B53]", l: "Confermata" },
    cancelled: { bg: "bg-[#E5DCD0]", fg: "text-[#7A7369]", l: "Annullata" },
  }[status] || { bg: "bg-[#E5DCD0]", fg: "text-[#7A7369]", l: status };
  return <span className={`inline-flex rounded-full px-3 py-1 text-[11px] font-bold ${cfg.bg} ${cfg.fg}`}>{cfg.l}</span>;
}
function SuggestionPill({ status }) {
  const cfg = {
    new: { bg: "bg-[#C8795A]/15", fg: "text-[#C8795A]", l: "NUOVA" },
    reviewed: { bg: "bg-[#E4B871]/25", fg: "text-[#8B6514]", l: "VALUTATA" },
    contacted: { bg: "bg-[#4A6B53]/15", fg: "text-[#4A6B53]", l: "CONTATTATA" },
    dismissed: { bg: "bg-[#E5DCD0]", fg: "text-[#7A7369]", l: "SCARTATA" },
  }[status] || { bg: "bg-[#E5DCD0]", fg: "text-[#7A7369]", l: status };
  return <span className={`inline-flex rounded-full px-3 py-1 text-[11px] font-bold ${cfg.bg} ${cfg.fg}`}>{cfg.l}</span>;
}

function EditModal({ initial, activities, onClose, onSave }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(f);
    setSaving(false);
  };
  const toggleAct = (s) => setF((x) => ({ ...x, activities: x.activities.includes(s) ? x.activities.filter((a) => a !== s) : [...x.activities, s] }));

  return (
    <div className="fixed inset-0 z-50 bg-[#2C312E]/60 backdrop-blur-sm flex items-start md:items-center justify-center p-0 md:p-6 overflow-y-auto" onClick={onClose}>
      <form onSubmit={onSubmit} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-3xl md:rounded-[2rem] rounded-t-[2rem] p-6 md:p-10 my-0 md:my-8">
        <div className="flex justify-between items-start mb-6">
          <h3 className="font-display font-black text-2xl text-[#2C312E]">{f.id ? "Modifica fattoria" : "Nuova fattoria"}</h3>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full border-2 border-[#E5DCD0]">✕</button>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <F l="Nome" v={f.name} o={(v) => setF({ ...f, name: v })} required/>
          <F l="Referente" v={f.contact_person} o={(v) => setF({ ...f, contact_person: v })}/>
          <F l="Indirizzo" v={f.address} o={(v) => setF({ ...f, address: v })} required/>
          <F l="Città" v={f.city} o={(v) => setF({ ...f, city: v })} required/>
          <F l="Provincia" v={f.province} o={(v) => setF({ ...f, province: v })} required/>
          <F l="Regione" v={f.region} o={(v) => setF({ ...f, region: v })} required/>
          <F l="CAP" v={f.postal_code} o={(v) => setF({ ...f, postal_code: v })} required/>
          <F l="Paese" v={f.country} o={(v) => setF({ ...f, country: v })}/>
          <F l="Lat" type="number" step="0.0001" v={f.lat} o={(v) => setF({ ...f, lat: parseFloat(v) || 0 })} required/>
          <F l="Lng" type="number" step="0.0001" v={f.lng} o={(v) => setF({ ...f, lng: parseFloat(v) || 0 })} required/>
          <F l="Telefono" v={f.phone} o={(v) => setF({ ...f, phone: v })}/>
          <F l="Email" type="email" v={f.email} o={(v) => setF({ ...f, email: v })}/>
          <F l="Sito web" v={f.website} o={(v) => setF({ ...f, website: v })}/>
          <F l="Facebook (URL)" v={f.facebook} o={(v) => setF({ ...f, facebook: v })}/>
          <F l="Instagram (URL o @handle)" v={f.instagram} o={(v) => setF({ ...f, instagram: v })}/>
        </div>
        <div className="mt-4">
          <ImageUploader
            label="Immagine di copertina"
            value={f.cover_image}
            onChange={(url) => setF({ ...f, cover_image: url })}
            testid="cover-uploader"
          />
        </div>
        <div className="mt-4">
          <GalleryUploader
            value={f.gallery || []}
            onChange={(list) => setF({ ...f, gallery: list })}
            testid="gallery-uploader"
          />
        </div>
        <div className="mt-4">
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-[#7A7369]">Tipologia di realtà</span>
            <select
              value={f.entity_type || ""}
              onChange={(e) => setF({ ...f, entity_type: e.target.value, ...(e.target.value !== "altro" ? { entity_type_custom: "" } : {}) })}
              className="mt-2 w-full h-12 rounded-xl border-2 border-[#E5DCD0] bg-white px-4 focus:outline-none focus:ring-2 focus:ring-[#4A6B53]"
              data-testid="entity-type-select"
            >
              <option value="">— Seleziona una tipologia —</option>
              {ENTITY_TYPES.map((et) => (
                <option key={et.slug} value={et.slug}>{et.name}</option>
              ))}
            </select>
            <span className="text-[11px] text-[#7A7369] mt-1 block">
              Sarà mostrata nella scheda pubblica prima della descrizione.
            </span>
          </label>
          {f.entity_type === "altro" && (
            <div className="mt-3">
              <F l="Descrivi la tua tipologia" v={f.entity_type_custom} o={(v) => setF({ ...f, entity_type_custom: v })} required/>
            </div>
          )}
        </div>
        <label className="block mt-4">
          <span className="text-xs font-bold uppercase tracking-wider text-[#7A7369]">Descrizione</span>
          <textarea required value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={3}
            className="mt-2 w-full rounded-2xl border-2 border-[#E5DCD0] bg-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#4A6B53]"/>
        </label>
        <div className="mt-4">
          <span className="text-xs font-bold uppercase tracking-wider text-[#7A7369]">Attività</span>
          <div className="mt-2 flex flex-wrap gap-2">
            {activities.map((a) => (
              <button type="button" key={a.slug} onClick={() => toggleAct(a.slug)}
                className={`rounded-full border px-3 py-1.5 text-xs font-bold ${f.activities.includes(a.slug) ? "bg-[#4A6B53] text-white border-[#4A6B53]" : "border-[#E5DCD0] text-[#58625B]"}`}>
                {a.name}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-6 flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="rounded-full border-2 border-[#E5DCD0] px-5 py-2.5 text-sm font-bold">Annulla</button>
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-[#4A6B53] text-white px-6 py-2.5 text-sm font-bold hover:bg-[#3D5844] disabled:opacity-60" data-testid="company-save">
            {saving && <Loader2 size={14} className="animate-spin"/>} Salva
          </button>
        </div>
      </form>
    </div>
  );
}

function F({ l, v, o, type = "text", required, step }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-[#7A7369]">{l}</span>
      <input type={type} step={step} value={v ?? ""} onChange={(e) => o(e.target.value)} required={required}
        className="mt-2 w-full h-12 rounded-xl border-2 border-[#E5DCD0] bg-white px-4 focus:outline-none focus:ring-2 focus:ring-[#4A6B53]"/>
    </label>
  );
}

function ProjectEditModal({ initial, companies, onClose, onSave }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(f);
    setSaving(false);
  };
  const toggleCompany = (id) => setF((x) => ({
    ...x,
    linked_companies: x.linked_companies.includes(id)
      ? x.linked_companies.filter((c) => c !== id)
      : [...x.linked_companies, id],
  }));

  return (
    <div className="fixed inset-0 z-50 bg-[#2C312E]/60 backdrop-blur-sm flex items-start md:items-center justify-center p-0 md:p-6 overflow-y-auto" onClick={onClose}>
      <form onSubmit={onSubmit} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-3xl md:rounded-[2rem] rounded-t-[2rem] p-6 md:p-10 my-0 md:my-8">
        <div className="flex justify-between items-start mb-6">
          <h3 className="font-display font-black text-2xl text-[#2C312E]">{f.id ? "Modifica progetto" : "Nuovo progetto"}</h3>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full border-2 border-[#E5DCD0]">✕</button>
        </div>

        <div className="space-y-4">
          <F l="Titolo del progetto" v={f.title} o={(v) => setF({ ...f, title: v })} required/>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-[#7A7369]">Estratto (mostrato nelle anteprime)</span>
            <textarea required value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} rows={2}
              className="mt-2 w-full rounded-2xl border-2 border-[#E5DCD0] bg-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#4A6B53]"/>
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-[#7A7369]">Contenuto</span>
            <textarea required value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} rows={10}
              placeholder="Separa i paragrafi con una riga vuota per andare a capo nel testo pubblico."
              className="mt-2 w-full rounded-2xl border-2 border-[#E5DCD0] bg-white px-4 py-3 text-base leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#4A6B53]"/>
          </label>

          <div className="grid md:grid-cols-3 gap-3">
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-[#7A7369]">Stato</span>
              <select
                value={f.status}
                onChange={(e) => setF({ ...f, status: e.target.value })}
                className="mt-2 w-full h-12 rounded-xl border-2 border-[#E5DCD0] bg-white px-4 focus:outline-none focus:ring-2 focus:ring-[#4A6B53]"
                data-testid="project-status"
              >
                <option value="current">In corso</option>
                <option value="upcoming">In arrivo (futuro)</option>
                <option value="past">Realizzato (passato)</option>
              </select>
            </label>
            <F l="Data inizio" type="date" v={f.start_date} o={(v) => setF({ ...f, start_date: v })}/>
            <F l="Data fine" type="date" v={f.end_date} o={(v) => setF({ ...f, end_date: v })}/>
          </div>

          <ImageUploader
            label="Immagine di copertina"
            value={f.cover_image}
            onChange={(url) => setF({ ...f, cover_image: url })}
            testid="project-cover-uploader"
          />
          <GalleryUploader
            value={f.gallery || []}
            onChange={(list) => setF({ ...f, gallery: list })}
            testid="project-gallery-uploader"
          />

          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-[#7A7369]">Fattorie collegate (opzionale)</span>
            <div className="mt-2 flex flex-wrap gap-2">
              {companies.map((c) => (
                <button
                  type="button"
                  key={c.id}
                  onClick={() => toggleCompany(c.id)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-bold ${f.linked_companies.includes(c.id) ? "bg-[#4A6B53] text-white border-[#4A6B53]" : "border-[#E5DCD0] text-[#58625B]"}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-8 flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="rounded-full border-2 border-[#E5DCD0] px-5 py-2.5 text-sm font-bold">Annulla</button>
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-[#4A6B53] text-white px-6 py-2.5 text-sm font-bold hover:bg-[#3D5844] disabled:opacity-60" data-testid="project-save">
            {saving && <Loader2 size={14} className="animate-spin"/>} Salva progetto
          </button>
        </div>
      </form>
    </div>
  );
}
